import React, { useMemo, useState } from "react";
import ElectricalQuestionCard, { isMandatory, needsRecommendation } from "./ElectricalQuestionCard";

/**
 * The assessment pane — pass one, done standing up. Modeled on
 * Csa/CsaAssess.js, one level shallower: Electrical's checklist is
 * area > category > checkpoint, with no middle "element" tier, so each
 * category section lists its checkpoints directly instead of grouping them
 * under elements first.
 *
 * Left rail: areas with live counts, used to jump. Main pane: one category at
 * a time — the unit an auditor actually walks, and the unit bulk-compliant
 * and Mark N/A act on.
 *
 * Category names are NOT unique across areas in the real checklist (e.g.
 * "TR-Signage" appears under several areas), so "current" and every lookup
 * below is keyed on the (area, category) pair, never on category name alone.
 */
export default function ElectricalAssess({
  framework,
  auditId,
  photos,
  onPhotosChanged,
  answers,
  counts,
  excludedAreas,
  excludedCategories,
  active,
  setActive,
  readOnly,
  previous,
  onConfirm,
  onAnswer,
  onObservation,
  onRecommendation,
  onBulkCompliant,
  onExclude,
  onRestoreScope,
}) {
  const [scopeTarget, setScopeTarget] = useState(null);

  /** Flat order of categories, so Previous and Next cross area boundaries. */
  const order = useMemo(() => {
    const out = [];
    (framework?.areas || []).forEach((a) =>
      (a?.categories || []).forEach((cat) => out.push({ area: a, cat }))
    );
    return out;
  }, [framework]);

  const catKey = (area, category) => area + " " + category;

  const at = order.findIndex(
    (o) => active && o.area.area === active.area && o.cat.category === active.category
  );
  const current = at >= 0 ? order[at] : order[0];
  const areaExclusion = current ? excludedAreas[current.area.area] : null;
  const categoryExclusion = current
    ? excludedCategories[catKey(current.area.area, current.cat.category)]
    : null;
  const exclusion = areaExclusion || categoryExclusion;

  /*
   * Conformance on this screen is computed on screen, from the answers
   * actually on file right now - not read from the server's rollup, which
   * only refreshes a couple of seconds after a save (see scheduleRollup in
   * ElectricalAudit.js) and was never confirmed to carry a percentage at
   * category level at all. This updates the instant a status is tapped.
   *
   * Same value scheme the backend's own scoring design uses elsewhere in
   * this app: Yes = 1, Partial = 0.5, No = 0, N/A excluded entirely (it is
   * not a 0 - it simply never joins the denominator). A checkpoint with no
   * status yet does not count either; a category nobody has touched reads
   * as "nothing to show" rather than 0%.
   */
  const STATUS_VALUE = { YES: 1, PARTIAL: 0.5, NO: 0 };

  const localConformance = (checkpoints) => {
    let sum = 0;
    let count = 0;
    (checkpoints || []).forEach((cp) => {
      const st = (answers[cp.code] || {}).status;
      if (!st) return;
      const key = String(st).toUpperCase();
      if (key === "NA" || !(key in STATUS_VALUE)) return;
      sum += STATUS_VALUE[key];
      count += 1;
    });
    return count ? Math.round((sum / count) * 100) : null;
  };

  const categoryConformance = useMemo(() => {
    const m = {};
    (framework?.areas || []).forEach((a) => {
      (a.categories || []).forEach((cat) => {
        m[catKey(a.area, cat.category)] = localConformance(cat.checkpoints);
      });
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framework, answers]);

  const blanksHere = current?.cat?.checkpoints?.filter(
    (cp) => !(answers[cp.code] || {}).status
  ).length;

  /*
   * Anything other than Yes needs an Observation on file; Recommendation
   * too unless it's N/A, which has nothing to recommend fixing (same rule
   * ElectricalQuestionCard's own fields enforce - isMandatory/
   * needsRecommendation) - while any checkpoint in the category being
   * walked right now is missing what it needs, moving on (Next/Previous,
   * or jumping to a different area/category) stays blocked. Checked here,
   * not just in the card, because leaving is a navigation decision this
   * screen owns, not the card.
   */
  const incompleteHere = (current?.cat?.checkpoints || []).filter((cp) => {
    const ans = answers[cp.code] || {};
    if (!isMandatory(ans.status)) return false;
    if (!String(ans.observation || "").trim()) return true;
    return needsRecommendation(ans.status) && !String(ans.recommendation || "").trim();
  }).length;

  const categoryCheckpointCount = current?.cat?.checkpoints?.length;
  const areaCheckpointCount = (current?.area?.categories || []).reduce(
    (sum, cat) => sum + (cat.checkpoints?.length || 0),
    0
  );

  /*
   * Next/Previous land on the new category, but not necessarily where the
   * auditor still has work to do in it - a category already partly answered
   * (carried forward, or revisited) would otherwise open at the top, past
   * whatever is still blank. Scrolls to the first unanswered checkpoint
   * instead, or the first one at all if every checkpoint here is answered.
   * The delay matches ElectricalWriteUp's onJump - a short wait for the new
   * category's cards to actually be on the page before scrolling to one.
   */
  const goTo = (target) => {
    setActive({ area: target.area.area, category: target.cat.category });
    const checkpoints = target.cat.checkpoints || [];
    const pending = checkpoints.find((cp) => !(answers[cp.code] || {}).status);
    const code = (pending || checkpoints[0])?.code;
    if (!code) return;
    setTimeout(() => {
      const el = document.getElementById("q-" + code);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };
  // console.log('order.length',order.length);
  // console.log('at',at)
  // console.log('current: ',current);
  return (
    <div className="cap-body">
      {/* ---------------- left rail ---------------- */}
      <nav className="cap-rail" aria-label="Areas">
        <p className="rail-hd">Areas</p>
        {(framework?.areas || []).map((a) => {
          const c = counts?.byArea[a.area] || { done: 0, total: 0 };
          const pctDone = c?.total ? Math.round((c.done / c.total) * 100) : 0;
          const inThisArea = current?.area?.area === a.area;

          return (
            <div className={"rail-area" + (inThisArea ? " act" : "")} key={a.area_no ?? a.area}>
              <div className="aname">
                <span>A{a.area_no} {shorten(a.area)}</span>
                <span className="ameta">
                  {c.done}/{c.total}
                </span>
              </div>
              <div className="rail-bar">
                <i style={{ width: pctDone + "%" }} />
              </div>

              {(a?.categories || []).map((cat) => {
                const key = catKey(a.area, cat.category);
                const sc = counts.byCategory[key] || { done: 0, total: 0 };
                const isExcluded = !!excludedAreas[a.area] || !!excludedCategories[key];
                const openCarry = cat?.checkpoints?.reduce((n, cp) => {
                  const ans = answers[cp.code];
                  return n + (ans && ans.carried_from_audit_id && !ans.is_confirmed ? 1 : 0);
                }, 0);
                const isActive = current?.area?.area === a.area && current?.cat?.category === cat.category;
                const cls = [
                  "rail-category",
                  isActive ? "active" : "",
                  isExcluded ? "excluded" : "",
                  sc.done === sc.total && sc.total > 0 ? "done" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <button
                    type="button"
                    key={key}
                    className={cls}
                    disabled={incompleteHere > 0 && !isActive}
                    title={
                      incompleteHere > 0 && !isActive
                        ? "Finish the Observation and Recommendation on the current category first"
                        : undefined
                    }
                    onClick={() => setActive({ area: a.area, category: cat.category })}
                  >
                    <span>{cat.category}</span>
                    <span className="cnt">
                      {isExcluded ? "N/A" : `${sc?.done}/${sc?.total}`}
                    </span>
                    {openCarry ? (
                      <span className="cnt-carry" title={openCarry + " carried, not yet checked"}>
                        {openCarry}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* ---------------- main pane ---------------- */}
      <main className="cap-main">
        <div className="cat-head">
          <div>
            <div className="crumb">
              A{current?.area?.area_no} · {current?.area?.area}
            </div>
            <h2>
              {current?.cat?.category}
              
              {current && categoryConformance[catKey(current.area.area, current.cat.category)] != null ? (
                <span className="cat-pct">
                  {categoryConformance[catKey(current.area.area, current.cat.category)]}% conformance
                </span>
              ) : null}
            </h2>
            {categoryCheckpointCount ? (
                <span className="cat-count">
                  {categoryCheckpointCount} Checkpoint{categoryCheckpointCount === 1 ? "" : "s"}
                </span>
              ) : null}
          </div>

          <div className="cat-actions">
            <button
              type="button"
              className="small-btn"
              disabled={readOnly || !!exclusion || blanksHere === 0}
              title="Sets only the checkpoints you have not answered"
              onClick={() =>
                onBulkCompliant("category", current?.cat?.category, current?.area?.area)
              }
            >
              Mark rest of category yes{blanksHere ? ` (${blanksHere})` : ""}
            </button>
            <button
              type="button"
              className="small-btn warn"
              disabled={readOnly || !!exclusion}
              onClick={() =>
                setScopeTarget({
                  level: "category",
                  value: current?.cat?.category,
                  area: current?.area?.area,
                  count: categoryCheckpointCount,
                })
              }
            >
              Mark category N/A
            </button>
            <button
              type="button"
              className="small-btn warn"
              disabled={readOnly || !!areaExclusion}
              onClick={() =>
                setScopeTarget({
                  level: "area",
                  value: current?.area?.area,
                  count: areaCheckpointCount,
                })
              }
            >
              Mark area N/A
            </button>
          </div>
        </div>

        {exclusion ? (
          <div className="scope-banner">
            <div>
              <strong>Out of scope.</strong>{" "}
              <span className="why">{exclusion?.justification}</span>
            </div>
            <button
              type="button"
              className="small-btn"
              style={{ marginLeft: "auto" }}
              disabled={readOnly}
              onClick={() => onRestoreScope(exclusion?.id)}
            >
              Put back in scope
            </button>
          </div>
        ) : null}

        <section className="element">
          {(current?.cat?.checkpoints || []).map((cp) => (
            <ElectricalQuestionCard
              key={cp.code}
              checkpoint={cp}
              auditId={auditId}
              photos={photos}
              onPhotosChanged={onPhotosChanged}
              answer={answers[cp.code]}
              previous={previous ? previous[cp.code] : null}
              onConfirm={onConfirm}
              disabled={readOnly}
              scopedOutBy={
                (answers[cp.code] || {}).na_source && exclusion ? exclusion.justification : null
              }
              onAnswer={onAnswer}
              onObservation={onObservation}
              onRecommendation={onRecommendation}
            />
          ))}
        </section>

        {incompleteHere > 0 ? (
          <div className="scope-banner">
            <div>
              <strong>Finish before moving on.</strong>{" "}
              <span className="why">
                {incompleteHere} checkpoint{incompleteHere === 1 ? "" : "s"} marked above still
                need{incompleteHere === 1 ? "s" : ""} both an Observation and a Recommendation.
              </span>
            </div>
          </div>
        ) : null}
      </main>

      {/* ---------------- footer nav ---------------- */}
      <div className="cap-foot">
        <button
          type="button"
          className="small-btn"
          disabled={at <= 0 || incompleteHere > 0}
          onClick={() => goTo(order[at - 1])}
        >
          ‹ Previous
        </button>
        <span className="where">
          {at + 1} of {order.length} categories
        </span>
        <button
          type="button"
          className="small-btn"
          disabled={(order.length > 1 && at >= order.length - 1) || incompleteHere > 0}
          onClick={() => goTo(order.length > 1 ? order[at + 1] : order[0])}
        >
          Next ›
        </button>
      </div>

      {scopeTarget ? (
        <ScopeDialog
          target={scopeTarget}
          onCancel={() => setScopeTarget(null)}
          onConfirm={(justification) => {
            onExclude(scopeTarget.level, scopeTarget.value, justification, scopeTarget.area);
            setScopeTarget(null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The justification prints in the client's report against every checkpoint
 * it covers, so the client can see what was not assessed and why. That is
 * also why it cannot be skipped.
 */
function ScopeDialog({ target, onCancel, onConfirm }) {
  const [text, setText] = useState("");
  const ready = text.trim().length >= 10;

  const scope = target.level === "area" ? "area" : "category";

  return (
    <>
      <div className="elec-pad-scrim" onClick={onCancel} />
      <div className="scope-dialog" role="dialog" aria-modal="true">
        <h3>Mark this {scope} not applicable</h3>

        <p className="scope-what">
          <span className="scope-level">{scope}</span>
          <strong>{target.value}</strong>
          {target.count ? (
            <span className="scope-count">
              {target.count} checkpoint{target.count === 1 ? "" : "s"}
            </span>
          ) : null}
        </p>

        <p>
          Every unanswered checkpoint here becomes N/A and drops out of the
          score. Answers you have already given are kept as they are.
        </p>
        <label htmlFor="scope-why">Reason — this is printed in the report</label>
        <textarea
          id="scope-why"
          rows={3}
          value={text}
          placeholder="e.g. No transformer yard on this site."
          onChange={(e) => setText(e.target.value)}
        />
        <div className="scope-actions">
          <button type="button" className="small-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="cap-btn" disabled={!ready} onClick={() => onConfirm(text.trim())}>
            Mark not applicable
          </button>
        </div>
      </div>
    </>
  );
}

/** The rail is 260px; full area names (or checkpoint titles) wrap and push it out. */
function shorten(name, max = 26) {
  const s = String(name || "");
  return s.length > max ? s.slice(0, max - 2).trimEnd() + "…" : s;
}
